import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Offcanvas, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as apiDocument from 'api/apiDocument';
import { useToast } from 'context/ToastContext';
import useSettingsParams from 'hooks/useSettingsParams';
import ESpinner from 'components/others/Spinner';
import TabAddPayment from './tabs/TabAddPayment';
import TabPaymentsList from './tabs/TabPaymentsList';
import { safeFloatParse } from 'utils/helpers/safeParsing';

export interface DocumentPaymentTarget {
    generationId: number;
    title: string;
}

interface Props {
    objectId: number;
    showTarget: DocumentPaymentTarget | null;
    setShowTarget: Dispatch<SetStateAction<DocumentPaymentTarget | null>>;
    onPaid?: () => Promise<void> | void;
}

const OffDocumentPayments = ({ objectId, showTarget, setShowTarget, onPaid }: Props) => {
    const { t } = useTranslation(['Objects']);
    const { showToast } = useToast();
    const params = useSettingsParams();
    const [activeTab, setActiveTab] = useState<'payments' | 'add-payment'>('add-payment');
    const [payments, setPayments] = useState<Record<string, any>[] | null>(null);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(false);
    const [recommendAmount, setRecommendAmount] = useState(0);
    const [isUsd, setIsUsd] = useState(false);

    const fetchPayments = async () => {
        if (!showTarget?.generationId || !objectId) return;
        setFetching(true);
        setError(false);
        const res = await apiDocument.getObjectDocumentPayments(objectId, showTarget.generationId);
        if (!res) {
            setError(true);
            setFetching(false);
            showToast(t('common:messError'), 'danger');
            return;
        }
        setPayments(res.payments || []);
        const balance = safeFloatParse(res.generation?.balance ?? 0);
        const amount = safeFloatParse(res.generation?.proposalAmount ?? 0);
        const paid = safeFloatParse(res.generation?.proposalPay ?? 0);
        setRecommendAmount(balance > 0 ? balance : Math.max(0, amount - paid));
        setIsUsd(Boolean(Number(res.generation?.proposalUsd ?? 0)));
        setFetching(false);
    };

    const handleHide = () => {
        setShowTarget(null);
        setActiveTab('add-payment');
        setPayments(null);
    };

    useEffect(() => {
        if (!params.objectPaymentControl) return;
        if (showTarget?.generationId) {
            setActiveTab('add-payment');
            void fetchPayments();
        }
    }, [showTarget?.generationId, params.objectPaymentControl]);

    if (!params.objectPaymentControl) return null;

    return (
        <Offcanvas className="off-import" show={showTarget !== null} onHide={handleHide} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{showTarget?.title || t('documents.payments.title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {fetching ? (
                    <ESpinner />
                ) : (
                    <Tabs
                        variant="underline"
                        id="off-document-payments-tab"
                        className="b-3 invoice-tab border-bottom"
                        activeKey={activeTab}
                        onSelect={(k) => k && setActiveTab(k as 'payments' | 'add-payment')}
                    >
                        <Tab
                            eventKey="add-payment"
                            title={
                                <>
                                    <i className="bi bi-plus-circle me-2 fs-14"></i>
                                    {t('documents.payments.tabAdd')}
                                </>
                            }
                        >
                            {showTarget ? (
                                <TabAddPayment
                                    objectId={objectId}
                                    generationId={showTarget.generationId}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab as Dispatch<SetStateAction<string>>}
                                    recommendAmount={recommendAmount}
                                    isUsd={isUsd}
                                    fetchPayments={fetchPayments}
                                    onPaid={onPaid}
                                    onClose={handleHide}
                                />
                            ) : null}
                        </Tab>
                        <Tab
                            eventKey="payments"
                            title={
                                <>
                                    <i className="bi bi-cash-coin me-2 fs-14"></i>
                                    {t('documents.payments.tabList')}
                                </>
                            }
                        >
                            <TabPaymentsList payments={payments} error={error} />
                        </Tab>
                    </Tabs>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffDocumentPayments;
